/* ==========================================================================
   hp-page-warranty-plan.js - live part lookup for the Protection Plan inset
   (sections/hp-page-warranty-plan.liquid). initLookup is copied from
   assets/warranty-page.js (read-only; that file is not loaded on this page)
   and adapted so the plan amounts land in the four tier cards instead of a
   rendered list:
   - /search/suggest?q=&section_id=warranty-product-suggest&resources[type]=product
     &resources[limit]=8 (debounced 250 ms, min 2 chars); the response list
     ([data-warranty-suggest-list] of [data-warranty-pick] buttons) is swapped in;
     the legacy listbox/option roles are stripped on the way in (a focusable button
     inside an option is invalid ARIA), the input is a role=combobox
   - tier math: planCents = floor(price * pct / 100), perYear = round(planCents
     / years / 100), thru = nowYear + years + 2; years/pct are read from the
     cards' data-tier-years / data-tier-pct (defaults [1,3,5,8] / [15,40,50,60])
   - blocked vendors, availability and the $200 threshold keep the legacy rules;
     a pick marked data-no-warranty="true" (product metafield custom.no_warranty,
     2026-10-21) gets its own neutral message (data-msg-no-warranty) that names
     no vendor, since the brand's other products can still qualify;
     message strings come from the data-msg-* attributes on [data-warranty-extended]
   Keyboard: ArrowDown moves from the field into the results, ArrowUp / ArrowDown
   walk the options, Escape closes. ES2017 (async/await like the legacy), defer-safe.
   ========================================================================== */
(function (win, doc) {
  'use strict';

  var DEFAULT_YEARS = [1, 3, 5, 8];
  var DEFAULT_PCTS = [15, 40, 50, 60];
  var MIN_CENTS = 20000;

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function formatMoney(cents, moneyFormat) {
    var amount = (Number(cents) / 100).toFixed(2);
    if (moneyFormat && moneyFormat.indexOf('{{') !== -1) {
      return moneyFormat
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(Number(cents) / 100)))
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    }
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(Number(cents) / 100);
    } catch (e) {
      return '$' + amount;
    }
  }

  function isBlockedVendor(vendor) {
    var v = String(vendor || '').toLowerCase();
    if (v.indexOf('allen') !== -1 && v.indexOf('bradley') !== -1) return true;
    if (v.indexOf('rockwell') !== -1) return true;
    if (v.indexOf('hengst') !== -1) return true;
    if (v.indexOf('altech') !== -1) return true;
    return false;
  }

  function fillVendor(template, vendor) {
    return String(template || '').replace(/\{vendor\}/g, vendor || 'this brand');
  }

  function initLookup(root) {
    var section = root.querySelector('[data-warranty-extended]');
    if (!section || section.getAttribute('data-hp-bound') === 'true') return;

    var lookup = section.querySelector('[data-warranty-lookup]');
    if (!lookup) return;

    var input = lookup.querySelector('[data-warranty-lookup-input]');
    var results = lookup.querySelector('[data-warranty-lookup-results]');
    var clearBtn = lookup.querySelector('[data-warranty-lookup-clear]');
    var status = lookup.querySelector('[data-warranty-lookup-status]');
    var resultBox = lookup.querySelector('[data-warranty-lookup-result]');
    var liveTiers = section.querySelector('[data-warranty-live-tiers]');
    var tierCards = liveTiers ? Array.prototype.slice.call(liveTiers.querySelectorAll('[data-tier-index]')) : [];
    var moneyFormat = section.getAttribute('data-money-format') || '${{amount}}';
    var nowYear = parseInt(section.getAttribute('data-now-year'), 10) || new Date().getFullYear();
    var minCents = parseInt(section.getAttribute('data-min-cents'), 10) || MIN_CENTS;
    var messages = {
      blocked: section.getAttribute('data-msg-blocked') || 'Extended warranty isn’t offered for {vendor}.',
      unavailable: section.getAttribute('data-msg-unavailable') || 'This product isn’t currently available for purchase in our store.',
      threshold: section.getAttribute('data-msg-threshold') || 'Extended plans are available on items $200 and up. This item is below that threshold.',
      noResults: section.getAttribute('data-msg-no-results') || 'No matching products found in our store. Try another SKU or title.',
      error: section.getAttribute('data-msg-error') || 'Unable to search right now. Please try again.',
      noWarranty: section.getAttribute('data-msg-no-warranty') || 'Extended plans aren’t offered on this item.'
    };

    if (!input || !results) return;
    section.setAttribute('data-hp-bound', 'true');

    function tierSpec(card, index) {
      var years = parseInt(card.getAttribute('data-tier-years'), 10);
      var pct = parseInt(card.getAttribute('data-tier-pct'), 10);
      if (!years || years < 0) years = DEFAULT_YEARS[index] || 0;
      if (!pct || pct < 0) pct = DEFAULT_PCTS[index] || 0;
      return { years: years, pct: pct };
    }

    function setStatus(msg, isError) {
      if (!status) return;
      if (!msg) {
        status.hidden = true;
        status.textContent = '';
        status.classList.remove('is-error');
        return;
      }
      status.hidden = false;
      status.textContent = msg;
      status.classList.toggle('is-error', !!isError);
    }

    function clearLiveTiers() {
      tierCards.forEach(function (card) {
        var live = card.querySelector('[data-warranty-tier-live]');
        if (live) {
          live.textContent = '';
          live.hidden = true;
        }
        card.classList.remove('has-live');
      });
    }

    function hideResult() {
      if (resultBox) resultBox.hidden = true;
      clearLiveTiers();
    }

    function openResults() {
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    function closeResults() {
      results.hidden = true;
      results.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
    }

    function toggleClear(show) {
      if (!clearBtn) return;
      clearBtn.hidden = !show;
    }

    /* Legacy renderLiveTiers wrote a list; here the amounts land in the cards. */
    function renderLiveTiers(priceCents) {
      tierCards.forEach(function (card, index) {
        var spec = tierSpec(card, index);
        var live = card.querySelector('[data-warranty-tier-live]');
        if (!live || !spec.years || !spec.pct) return;
        var planCents = Math.floor((priceCents * spec.pct) / 100);
        var perYear = Math.round(planCents / spec.years / 100);
        var thru = nowYear + spec.years + 2;
        live.textContent = formatMoney(planCents, moneyFormat) + ' total · $' + perYear + '/yr · through ' + thru;
        live.hidden = false;
        card.classList.add('has-live');
      });
    }

    function showProduct(data) {
      if (!resultBox) return;
      var img = resultBox.querySelector('[data-warranty-result-image]');
      var title = resultBox.querySelector('[data-warranty-result-title]');
      var sku = resultBox.querySelector('[data-warranty-result-sku]');
      var price = resultBox.querySelector('[data-warranty-result-price]');
      var link = resultBox.querySelector('[data-warranty-result-link]');

      if (title) title.textContent = data.title || '';
      if (sku) {
        var bits = [];
        if (data.sku) bits.push('SKU: ' + data.sku);
        if (data.vendor) bits.push(data.vendor);
        sku.textContent = bits.join(' · ');
      }
      if (price) price.textContent = formatMoney(data.price, moneyFormat);
      if (link) {
        link.href = data.url || '#';
        link.hidden = !data.url;
      }
      if (img) {
        if (data.image) {
          img.src = data.image;
          img.hidden = false;
        } else {
          img.removeAttribute('src');
          img.hidden = true;
        }
      }

      resultBox.hidden = false;
      renderLiveTiers(data.price);
    }

    function evaluatePick(btn) {
      var price = parseInt(btn.getAttribute('data-price'), 10) || 0;
      var vendor = btn.getAttribute('data-vendor') || '';
      var available = btn.getAttribute('data-available') === 'true';
      var title = btn.getAttribute('data-title') || '';
      var sku = btn.getAttribute('data-variant-sku') || btn.getAttribute('data-sku') || '';
      var url = btn.getAttribute('data-url') || '';
      var image = btn.getAttribute('data-image') || '';
      var noWarranty = btn.getAttribute('data-no-warranty') === 'true';

      closeResults();
      input.value = sku || title;
      toggleClear(true);
      input.focus();

      if (!available) {
        hideResult();
        setStatus(messages.unavailable, true);
        return;
      }
      if (noWarranty) {
        hideResult();
        setStatus(messages.noWarranty, true);
        return;
      }
      if (isBlockedVendor(vendor)) {
        hideResult();
        setStatus(fillVendor(messages.blocked, vendor), true);
        return;
      }
      if (price < minCents) {
        hideResult();
        setStatus(messages.threshold, true);
        return;
      }

      setStatus('');
      showProduct({
        title: title,
        sku: sku,
        vendor: vendor,
        price: price,
        url: url,
        image: image
      });
    }

    var requestSeq = 0;

    async function fetchResults(term) {
      var seq = ++requestSeq;
      try {
        var url =
          '/search/suggest?q=' +
          encodeURIComponent(term) +
          '&section_id=warranty-product-suggest&resources[type]=product&resources[limit]=8';
        var response = await fetch(url);
        if (!response.ok) throw new Error('Suggest failed');
        var html = await response.text();
        if (seq !== requestSeq) return;   /* a newer request is in flight */
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var list = parsed.querySelector('[data-warranty-suggest-list]');
        if (list && list.children.length) {
          results.innerHTML = list.outerHTML;
          /* The suggest markup is ul[role=listbox] > li[role=option] > button. Drop those
             roles here (the legacy page keeps its own copy): the buttons are the controls. */
          Array.prototype.forEach.call(results.querySelectorAll('[role="listbox"], [role="option"]'), function (el) {
            el.removeAttribute('role');
          });
          openResults();
          setStatus('');
        } else {
          closeResults();
          hideResult();
          setStatus(messages.noResults, true);
        }
      } catch (err) {
        if (seq !== requestSeq) return;
        closeResults();
        setStatus(messages.error, true);
      }
    }

    function optionButtons() {
      return Array.prototype.slice.call(results.querySelectorAll('[data-warranty-pick]'));
    }

    input.addEventListener(
      'input',
      debounce(function () {
        var term = input.value.trim();
        toggleClear(term.length > 0);
        hideResult();
        setStatus('');
        if (term.length < 2) {
          closeResults();
          return;
        }
        fetchResults(term);
      }, 250)
    );

    results.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-warranty-pick]');
      if (!btn) return;
      evaluatePick(btn);
    });

    results.addEventListener('keydown', function (event) {
      var options = optionButtons();
      if (!options.length) return;
      var idx = options.indexOf(doc.activeElement);
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        options[Math.min(idx + 1, options.length - 1)].focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (idx <= 0) { input.focus(); } else { options[idx - 1].focus(); }
      } else if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        closeResults();
        input.focus();
      }
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        input.value = '';
        toggleClear(false);
        closeResults();
        hideResult();
        setStatus('');
        input.focus();
      });
    }

    doc.addEventListener('click', function (event) {
      if (!lookup.contains(event.target)) closeResults();
    });

    input.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        closeResults();
      } else if (event.key === 'ArrowDown' && !results.hidden) {
        var first = optionButtons()[0];
        if (first) {
          event.preventDefault();
          first.focus();
        }
      } else if (event.key === 'Enter') {
        /* the combobox input sits in no form: Enter must not reload; pick the only option if there is one */
        event.preventDefault();
        var options = optionButtons();
        if (!results.hidden && options.length === 1) evaluatePick(options[0]);
      }
    });
  }

  function boot(scope) {
    var ctx = scope && scope.querySelectorAll ? scope : doc;
    Array.prototype.forEach.call(ctx.querySelectorAll('.hp-page-warranty-plan'), initLookup);
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', function () { boot(doc); });
  } else {
    boot(doc);
  }
  doc.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})(window, document);
